import { make } from "./dom";
import type { Panel } from "./panel";

export interface CapturePriceRow {
  label: string;
  value: string;
}

export interface CaptureDetail {
  preview?: HTMLCanvasElement | null;
  prices: readonly CapturePriceRow[];
}

export type OpenCapture = (blob: Blob | null, title: string, detail?: CaptureDetail) => void;

export function mountCapturePanel(panel: Panel): OpenCapture {
  const title = make("p", "capture-title");
  const image = make("img", "capture-image");
  const preview = make("div", "capture-preview");
  const unavailable = make("p", "capture-unavailable", "Full scan unavailable");
  const prices = make("dl", "capture-prices");
  const download = make("a", "button button--ghost", "Save image");
  panel.body.append(title, image, preview, unavailable, prices, download);
  let url: string | null = null;
  const release = (): void => {
    image.removeAttribute("src");
    download.removeAttribute("href");
    download.onclick = null;
    preview.replaceChildren();
    prices.replaceChildren();
    if (url) URL.revokeObjectURL(url);
    url = null;
  };
  panel.onClose = release;
  return (blob, label, detail) => {
    release();
    title.textContent = label;
    image.hidden = !blob;
    preview.hidden = !!blob || !detail?.preview;
    unavailable.hidden = !!blob;
    download.hidden = !blob;
    prices.hidden = !detail?.prices.length;
    if (detail) {
      for (const entry of detail.prices) {
        prices.append(
          make("dt", "capture-price-label", entry.label),
          make("dd", "capture-price-value", entry.value),
        );
      }
      if (!blob && detail.preview) {
        const copy = make("canvas", "capture-preview-image");
        copy.width = detail.preview.width;
        copy.height = detail.preview.height;
        copy.getContext("2d")?.drawImage(detail.preview, 0, 0);
        copy.setAttribute("role", "img");
        copy.setAttribute("aria-label", `Scan preview: ${label}`);
        preview.append(copy);
      }
    }
    if (blob) {
      url = URL.createObjectURL(blob);
      image.src = url;
      image.alt = `Captured scan: ${label}`;
      download.href = url;
      download.download = "webscan2-capture.jpg";
      // A separate URL survives closing the panel immediately after a download tap.
      download.onclick = () => {
        const fileUrl = URL.createObjectURL(blob);
        download.href = fileUrl;
        window.setTimeout(() => URL.revokeObjectURL(fileUrl), 1000);
      };
    }
    panel.open();
  };
}
